import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect } from 'react';
import { useAtom } from 'jotai';
import { usersAtom } from '../utils/atoms';
import { useNavigate } from 'react-router-dom';
import { formatPhoneNumber } from '../utils/phoneNumber';
import LetterAvatar from './child/LetterAvatar';

const ViewProfileLayer = ({ userId }) => {
    const [users] = useAtom(usersAtom);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        if (userId && users.length > 0) {
            const foundUser = users.find(u => u.id.toString() === userId);
            if (foundUser) {
                setUser(foundUser);
            } else {
                // User not found, redirect back to users list
                navigate('/users-list');
            }
            setLoading(false);
        } else if (users.length === 0) {
            // Users not loaded yet, wait for them
            setLoading(true);
        } else {
            // No userId provided
            navigate('/users-list');
        }
    }, [userId, users, navigate]);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="text-center">
                    <Icon icon="mdi:account-question" className="text-xl text-neutral-400 mb-3" />
                    <p className="text-neutral-500">תלמיד לא נמצא</p>
                </div>
            </div>
        );
    }

    return (
        <div className="row gy-4">
            {/* Counters Section - Moved above the left card */}
            <div className="col-12">
                <div className="row g-3">
                    <div className="col-lg-4">
                        <div className="card shadow-none border bg-gradient-start-1 h-100">
                            <div className="card-body p-20">
                                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                                    <div>
                                        <p className="fw-medium text-primary-light mb-1">שיעורים</p>
                                        <h6 className="mb-0">{user.lessons}</h6>
                                    </div>
                                    <div className="w-50-px h-50-px bg-cyan rounded-circle d-flex justify-content-center align-items-center">
                                        <Icon
                                            icon="solar:book-bold"
                                            className="text-white text-2xl mb-0"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-4">
                        <div className="card shadow-none border bg-gradient-start-2 h-100">
                            <div className="card-body p-20">
                                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                                    <div>
                                        <p className="fw-medium text-primary-light mb-1">מבחנים</p>
                                        <h6 className="mb-0">{user.passedTest}</h6>
                                    </div>
                                    <div className="w-50-px h-50-px bg-purple rounded-circle d-flex justify-content-center align-items-center">
                                        <Icon
                                            icon="solar:document-bold"
                                            className="text-white text-2xl mb-0"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-4">
                        <div className="card shadow-none border bg-gradient-start-3 h-100">
                            <div className="card-body p-20">
                                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                                    <div>
                                        <p className="fw-medium text-primary-light mb-1">מבחנים פנימיים</p>
                                        <h6 className="mb-0">{user.internalTests || 0}</h6>
                                    </div>
                                    <div className="w-50-px h-50-px bg-info rounded-circle d-flex justify-content-center align-items-center">
                                        <Icon
                                            icon="solar:clipboard-text-bold"
                                            className="text-white text-2xl mb-0"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="col-lg-4">
                <div className="user-grid-card position-relative border radius-16 overflow-hidden bg-base">
                    <img
                        src="assets/images/user-grid/user-grid-bg1.png"
                        alt=""
                        className="w-100 object-fit-cover"
                    />
                    <div className="pb-24 ms-16 mb-24 me-16">
                        <div className="text-center border border-top-0 border-start-0 border-end-0 d-flex flex-column align-items-center justify-content-center py-3">
                            <LetterAvatar
                                letter={user.name}
                                size="100px"
                                className="border border-white border-width-2-px"
                            />
                            <h6 className="mb-2 mt-16">{user.name}</h6>
                            <div className="mt-2">
                                <span className={`px-12 py-2 radius-4 fw-medium text-sm ${user.status === 'פעיל'
                                    ? 'bg-success-focus text-success-600 border border-success-main'
                                    : 'bg-neutral-200 text-neutral-600 border border-neutral-400'
                                    }`}>
                                    {user.status}
                                </span>
                            </div>
                        </div>
                        <div className="mt-24">
                            <h6 className="text-xl mb-16">מידע אישי</h6>
                            <ul>
                                <li className="d-flex align-items-center gap-1 mb-12">
                                    <span className="w-30 text-md fw-semibold text-primary-light">
                                        שם מלא
                                    </span>
                                    <span className="w-70 text-secondary-light fw-medium">
                                        {user.name}
                                    </span>
                                </li>

                                <li className="d-flex align-items-center gap-1 mb-12">
                                    <span className="w-30 text-md fw-semibold text-primary-light">
                                        מספר טלפון
                                    </span>
                                    <span className="w-70 text-secondary-light fw-medium">
                                        {formatPhoneNumber(user.phoneNumber)}
                                    </span>
                                </li>
                                <li className="d-flex align-items-center gap-1 mb-12">
                                    <span className="w-30 text-md fw-semibold text-primary-light">
                                        אזור לימוד
                                    </span>
                                    <span className="w-70 text-secondary-light fw-medium">
                                        {user.department}
                                    </span>
                                </li>
                                <li className="d-flex align-items-center gap-1 mb-12">
                                    <span className="w-30 text-md fw-semibold text-primary-light">
                                        תאריך הצטרפות
                                    </span>
                                    <span className="w-70 text-secondary-light fw-medium">
                                        {user.joinDate}
                                    </span>
                                </li>
                                <li className="d-flex align-items-center gap-1 mb-12">
                                    <span className="w-30 text-md fw-semibold text-primary-light">
                                        מספר זהות
                                    </span>
                                    <span className="w-70 text-secondary-light fw-medium">
                                        {user.idNumber}
                                    </span>
                                </li>
                                <li className="d-flex align-items-center gap-1">
                                    <span className="w-30 text-md fw-semibold text-primary-light">
                                        סטטוס
                                    </span>
                                    <span className="w-70 text-secondary-light fw-medium">
                                        {user.status}
                                    </span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-lg-8">
                <div className="card">
                    <div className="card-body p-24">
                        <ul
                            className="nav border-gradient-tab nav-pills mb-20 d-inline-flex"
                            id="pills-tab"
                            role="tablist"
                        >
                            <li className="nav-item" role="presentation">
                                <button
                                    className="nav-link d-flex align-items-center px-24 active"
                                    id="pills-academic-info-tab"
                                    data-bs-toggle="pill"
                                    data-bs-target="#pills-academic-info"
                                    type="button"
                                    role="tab"
                                    aria-controls="pills-academic-info"
                                    aria-selected="true"
                                >
                                    לימוד
                                </button>
                            </li>
                            <li className="nav-item" role="presentation">
                                <button
                                    className="nav-link d-flex align-items-center px-24"
                                    id="pills-financial-info-tab"
                                    data-bs-toggle="pill"
                                    data-bs-target="#pills-financial-info"
                                    type="button"
                                    role="tab"
                                    aria-controls="pills-financial-info"
                                    aria-selected="false"
                                    tabIndex={-1}
                                >
                                    תשלומים
                                </button>
                            </li>

                        </ul>
                        <div className="tab-content" id="pills-tabContent">
                            <div
                                className="tab-pane fade show active"
                                id="pills-academic-info"
                                role="tabpanel"
                                aria-labelledby="pills-academic-info-tab"
                                tabIndex={0}
                            >
                                <h6 className="text-md text-primary-light mb-16">פרטי לימוד</h6>
                                <div className="row">
                                    <div className="col-sm-6">
                                        <div className="mb-20">
                                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                                שיעורים
                                            </label>
                                            <div className="form-control radius-8 bg-light">
                                                {user.lessons}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <div className="mb-20">
                                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                                שיעורים קודמים
                                            </label>
                                            <div className="form-control radius-8 bg-light">
                                                {user.lessonsOffset}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <div className="mb-20">
                                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                                תיאוריה
                                            </label>
                                            <div className="form-control radius-8 bg-light">
                                                {user.theory ? 'כן' : 'לא'}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <div className="mb-20">
                                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                                עבר מבחן
                                            </label>
                                            <div className="form-control radius-8 bg-light">
                                                {user.passedTest ? 'כן' : 'לא'}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <div className="mb-20">
                                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                                טופס ירוק
                                            </label>
                                            <div className="form-control radius-8 bg-light">
                                                {user.greenForm ? 'כן' : 'לא'}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <div className="mb-20">
                                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                                משקפיים
                                            </label>
                                            <div className="form-control radius-8 bg-light">
                                                {user.glasses ? 'כן' : 'לא'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div
                                className="tab-pane fade"
                                id="pills-financial-info"
                                role="tabpanel"
                                aria-labelledby="pills-financial-info-tab"
                                tabIndex={0}
                            >
                                <h6 className="text-md text-primary-light mb-16">מידע פיננסי</h6>
                                <div className="row">
                                    <div className="col-sm-6">
                                        <div className="mb-20">
                                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                                מחיר מיוחד
                                            </label>
                                            <div className="form-control radius-8 bg-light">
                                                ₪{user.specialPrice}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <div className="mb-20">
                                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                                {user.paymentsBalance < 0 ? 'חוב' : "יתרה"}
                                            </label>
                                            <div className="form-control radius-8 bg-light">
                                                ₪{user.paymentsBalance}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <h6 className="text-md text-primary-light mb-16 mt-24">קרדיטים</h6>
                                <div className="row">
                                    <div className="col-sm-4">
                                        <div className="mb-20">
                                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                                קרדיט שיעורים
                                            </label>
                                            <div className="form-control radius-8 bg-light">
                                                {user.lessonsCredit === 'unlimited' ? 'ללא הגבלה' :
                                                    user.lessonsCredit || '0'}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-sm-4">
                                        <div className="mb-20">
                                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                                קרדיט מבחנים
                                            </label>
                                            <div className="form-control radius-8 bg-light">
                                                {user.testsCredit === 'unlimited' ? 'ללא הגבלה' :
                                                    user.testsCredit || '0'}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-sm-4">
                                        <div className="mb-20">
                                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                                קרדיט מבחנים פנימיים
                                            </label>
                                            <div className="form-control radius-8 bg-light">
                                                {user.internalTestsCredits === 'unlimited' ? 'ללא הגבלה' :
                                                    user.internalTestsCredits || '0'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ViewProfileLayer;